import React from 'react';
import { Link } from 'react-router-dom';
import './ForBusinesses.css';

const HOTSPOT_VISUALS = [
  { id: 'icon', label: 'Hotspot icon', image: '/images/for-businesses/webp/biz-hotspots.webp' },
  { id: 'map', label: 'Map visualisation', image: '/images/for-businesses/webp/biz-visualisation.webp' },
  { id: 'photo', label: 'Real image', image: '/images/for-businesses/webp/biz-realimg.webp' },
];

const GROUP_EXAMPLES = [
  { id: '1', label: 'Example 1', image: '/images/for-businesses/webp/biz-eg1.webp' },
  { id: '2', label: 'Example 2', image: '/images/for-businesses/webp/biz-eg2.webp' },
  { id: '3', label: 'Example 3', image: '/images/for-businesses/webp/biz-eg3.webp' },
];

const BROADCAST_EXAMPLES = [
  'Coffee tasting in 15 minutes',
  'Founder meetup begins at 5pm',
];

const SPACES = [
  'Café',
  'Co-working',
  'Universities',
  'Events',
  'Communities',
  'Innovation Hubs',
];

const CAPABILITIES = [
  {
    title: 'Create hotspots',
    description: 'Turn locations into interactive environments.',
  },
  {
    title: 'Create groups',
    description: 'Shape how people connect.',
  },
  {
    title: 'Send broadcasts',
    description: 'Reach people in and around your space.',
  },
  {
    title: 'Understand participation',
    description:
      'See what your audience engages with over time and deliver a better experience.',
  },
];

const BizImage = ({ src, alt, className, placeholderLabel }) => {
  const [failed, setFailed] = React.useState(!src);

  if (failed || !src) {
    return (
      <div className={`biz-placeholder ${className || ''}`} aria-hidden={!alt}>
        <span>{placeholderLabel}</span>
      </div>
    );
  }

  return (
    <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />
  );
};

const ForBusinesses = () => {
  return (
    <div className="biz-page">
      {/* Hero */}
      <section className="biz-hero" aria-labelledby="biz-hero-heading">
        <div className="biz-inner biz-hero-inner">
          <h1 id="biz-hero-heading" className="biz-headline">
            Turn footfall into interaction
          </h1>
          <p className="biz-subheadline">
            Give people more reason to engage with your space, your community, and each other.
          </p>
          <Link to="/contact" className="biz-btn biz-btn--primary">
            Book a demo
          </Link>
        </div>
      </section>

      {/* Section 1 */}
      <section className="biz-section" aria-labelledby="biz-s1-heading">
        <div className="biz-inner biz-narrow">
          <h2 id="biz-s1-heading" className="biz-headline biz-headline--section">
            Most spaces already have the people
          </h2>
          <p className="biz-body">
            The challenge isn't bringing people together. It's helping them discover opportunities
            to engage once they're already there.
          </p>
          <p className="biz-body">
            Whether it's a café, co-working space, university, community, or event, moments for
            interaction often go unnoticed.
          </p>
        </div>
      </section>

      {/* Section 2 — Hotspot */}
      <section className="biz-section biz-section--alt" aria-labelledby="biz-s2-heading">
        <div className="biz-inner">
          <div className="biz-split">
            <div className="biz-split-text">
              <h2 id="biz-s2-heading" className="biz-headline biz-headline--section">
                Create a hotspot
              </h2>
              <p className="biz-body">
                A hotspot is your space's interactive layer.
              </p>
              <p className="biz-body">
                It gives people a place to discover what's happening, participate in conversations,
                and engage with others around them.
              </p>
            </div>
            <div className="biz-visual-trio">
              {HOTSPOT_VISUALS.map((item) => (
                <div key={item.id} className="biz-visual-trio-item">
                  <div className="biz-visual-trio-media">
                    <BizImage
                      src={item.image}
                      alt={item.label}
                      className="biz-image biz-image--trio"
                      placeholderLabel={item.label}
                    />
                  </div>
                  <p className="biz-visual-caption">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 — Groups */}
      <section className="biz-section" aria-labelledby="biz-s3-heading">
        <div className="biz-inner">
          <h2 id="biz-s3-heading" className="biz-headline biz-headline--section biz-headline--center">
            Shape the experience with groups
          </h2>
          <p className="biz-body biz-body--center biz-narrow biz-narrow--center">
            Create intent-based spaces around the interactions you want to encourage.
          </p>
          <div className="biz-example-grid">
            {GROUP_EXAMPLES.map((item) => (
              <article key={item.id} className="biz-card biz-example-card">
                <BizImage
                  src={item.image}
                  alt={item.label}
                  className="biz-image biz-image--card"
                  placeholderLabel={item.label}
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — Broadcasts */}
      <section className="biz-section biz-section--alt" aria-labelledby="biz-s4-heading">
        <div className="biz-inner">
          <div className="biz-split biz-split--broadcast">
            <div className="biz-split-text">
              <h2 id="biz-s4-heading" className="biz-headline biz-headline--section">
                Reach people through broadcasts
              </h2>
              <p className="biz-body">
                Send updates, announcements, and invitations to people connected to your hotspot.
              </p>
              <ul className="biz-broadcast-examples" aria-label="Example broadcasts">
                {BROADCAST_EXAMPLES.map((text) => (
                  <li key={text} className="biz-broadcast-example">
                    {text}
                  </li>
                ))}
              </ul>
              <p className="biz-body">
                Choose who receives them, people already at your space, people who follow you, or
                even people nearby who would benefit. You're no longer limited to your existing
                network.
              </p>
            </div>
            <div className="biz-broadcast-visual">
              <BizImage
                src="/images/for-businesses/webp/biz-megaphone.webp"
                alt="Broadcast megaphone"
                className="biz-image biz-image--megaphone"
                placeholderLabel="Megaphone"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 5 — Spaces grid */}
      <section className="biz-section" aria-labelledby="biz-s5-heading">
        <div className="biz-inner">
          <h2 id="biz-s5-heading" className="biz-headline biz-headline--section biz-headline--center">
            Designed for spaces that bring people together
          </h2>
          <ul className="biz-spaces-grid">
            {SPACES.map((label) => (
              <li key={label} className="biz-space-pill">
                {label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Section 6 — What businesses can do */}
      <section className="biz-section biz-section--alt" aria-labelledby="biz-s6-heading">
        <div className="biz-inner">
          <h2 id="biz-s6-heading" className="biz-headline biz-headline--section biz-headline--center">
            What businesses can do
          </h2>
          <div className="biz-capabilities">
            {CAPABILITIES.map((item) => (
              <article key={item.title} className="biz-card biz-capability-card">
                <h3 className="biz-card-title">{item.title}</h3>
                <p className="biz-card-text">{item.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7 — Why businesses use PoppyGrid */}
      <section className="biz-section" aria-labelledby="biz-s7-heading">
        <div className="biz-inner">
          <h2 id="biz-s7-heading" className="biz-headline biz-headline--section biz-headline--center">
            Why businesses use PoppyGrid
          </h2>
          <div className="biz-why-grid">
            <article className="biz-card biz-why-card">
              <h3 className="biz-card-title">Stronger community</h3>
              <p className="biz-card-text">
                You get footfall, PoppyGrid helps turn it into a community.
              </p>
            </article>
            <article className="biz-card biz-why-card">
              <h3 className="biz-card-title">More memorable experiences</h3>
              <p className="biz-card-text">Create moments people remember.</p>
            </article>
          </div>
        </div>
      </section>

      {/* Section 8 — Book a call */}
      <section className="biz-section biz-cta" aria-labelledby="biz-s8-heading">
        <div className="biz-inner biz-cta-inner">
          <h2 id="biz-s8-heading" className="biz-headline biz-headline--cta">
            Book a call
          </h2>
          <p className="biz-body biz-body--cta">
            We help you set up everything — no complicated training, no additional hardware. Just a
            simple way to make your space more interactive.
          </p>
          <Link to="/contact" className="biz-btn biz-btn--light">
            Book a demo
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ForBusinesses;
